/**
 * The daily review requirement, as the screens use it (ticket 09).
 *
 * In the beta, the day's first new-moment request fixes up to five due cards;
 * new moments stay locked until each has a saved answer. Recording is never
 * blocked — only the analysis request is — so what the learner records waits
 * on this device. These helpers keep the wording identical on every screen.
 */
import { html, nothing } from 'lit';
import { HttpError } from './api';
import { resetTime } from './drafts-ui';
import { t } from './i18n';
import { go } from './router';
import type { Allowance, ReviewGate } from './types';

/** The review block of an allowance reply; undefined in personal mode. */
export function gateOf(a?: Allowance): ReviewGate | undefined {
  return a && a.limit !== null ? a.review : undefined;
}

/** A creation refused because today's review batch is still open (409 `daily-review`), else null. */
export function gateRefusal(e: unknown): { required: number; done: number; remaining: number } | null {
  if (!(e instanceof HttpError) || e.status !== 409 || e.body?.reason !== 'daily-review') return null;
  const n = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : 0);
  return { required: n(e.body.required), done: n(e.body.done), remaining: n(e.body.remaining) };
}

/** "Review 5 cards to unlock new moments today — 2 of 5 done". */
export function gateLine(g: { required: number; done: number }): string {
  return t(g.required === 1 ? 'gate.lineOne' : 'gate.lineMany', { n: g.required, done: g.done });
}

/**
 * The requirement card for home and record: progress plus the way to review.
 * Before today's batch is fixed, a quiet preview line at most; once it is done
 * or waived, nothing at all.
 */
export interface GateRenderOptions {
  hint?: boolean;
  dueCount?: number;
  roundCards?: number;
  timezone?: string;
}

/** The four count views, usable when another home card already owns the gate action. */
export function renderGateCounts(g: ReviewGate | undefined, opts: GateRenderOptions = {}) {
  if (!g || (opts.dueCount === undefined && opts.roundCards === undefined)) return nothing;
  const batch = g.status === 'none' ? (g.preview ?? 0) : g.status === 'waived' ? 0 : g.required;
  const progress = g.status === 'pending'
    ? t('gate.countPending', { done: g.done, required: g.required, remaining: g.remaining })
    : g.status === 'satisfied'
      ? t('gate.countComplete', { required: g.required })
      : g.status === 'waived'
      ? t('gate.countWaived')
      : t('gate.countNotFixed');
  return html`<div class="review-counts" aria-label=${t('gate.countsLabel')}>
        <div><span>${t('gate.countRequirement')}: </span><strong>${progress}</strong></div>
        <div><span>${t('gate.countBatch')}: </span><strong>${t('gate.countCards', { n: batch })}</strong></div>
        ${opts.dueCount !== undefined ? html`<div><span>${t('gate.countQueue')}: </span><strong>${t('gate.countCards', { n: opts.dueCount })}</strong></div>` : nothing}
        ${opts.roundCards !== undefined ? html`<div><span>${t('gate.countRound')}: </span><strong>${t('gate.countCards', { n: opts.roundCards })}</strong></div>` : nothing}
      </div>`;
}

export function renderGate(g: ReviewGate | undefined, opts: GateRenderOptions = {}) {
  if (!g) return nothing;
  const counts = renderGateCounts(g, opts);
  if (g.status === 'pending') {
    return html`
      <div class="card review-gate" role="status" style="margin-top:8px">
        <div><strong>${gateLine(g)}</strong></div>
        ${opts.hint ? html`<div class="muted small" style="margin-top:4px">${t('gate.recordHint')}</div>` : nothing}
        ${counts}
        ${g.resetsAt ? html`<div class="muted small review-reset" style="margin-top:4px">${t('gate.resetAt', { time: resetTime(g.resetsAt, opts.timezone), timezone: opts.timezone ?? t('gate.localTime') })}</div>` : nothing}
        <button class="btn primary block review-gate-go" style="margin-top:8px" @click=${() => go('/round')}>${t('gate.reviewNow')}</button>
      </div>`;
  }
  if (g.status === 'none' && (g.preview ?? 0) > 0) {
    return html`<div class="review-gate-preview-wrap">
      <div class="muted small review-gate-preview" style="text-align:center;margin-top:2px">
        ${t(g.preview === 1 ? 'gate.previewOne' : 'gate.previewMany', { n: g.preview ?? 0 })}
      </div>${counts}
    </div>`;
  }
  return counts;
}
